/**
 * O seletor de filial — `DESIGN_SYSTEM §7.2` e `§7.4`, `PWA §10.2`.
 *
 * ```
 * ┌───────────────────────────────────────────┐
 * │ 📍 Filial Centro                       ▾  │   cabecalho, 56 px
 * └───────────────────────────────────────────┘
 * ```
 *
 * §7.2: *"O seletor de filial e um **botao**, nao um `select` — abre sheet com lista, distancia,
 * horario, status de plantao"*. §7.4 confirma o `Sheet` (e nao `Modal`) para "escolher filial".
 *
 * ## Tres decisoes de implementacao que sao requisito
 *
 * 1. **O botao do cabecalho nao faz requisicao.** Ele desenha o nome que ja esta guardado
 *    (`preferencia.ts`) e nada mais. Um cabecalho que dependesse de `GET /v1/stores` poria uma
 *    requisicao no caminho critico de **toda** tela, inclusive offline, para exibir um texto que
 *    o aparelho ja conhece.
 * 2. **A folha so monta quando abre.** Ela e quem chama `GET /v1/stores`; enquanto ninguem toca
 *    no botao, nao ha hook de rede montado, nao ha query e nao ha bytes. E tambem o que permite
 *    ao casco existir em contexto sem cliente HTTP.
 * 3. **Status com texto, nunca so com cor** (§7.2, §3.4). "Aberta ate 22h" e "Fechada · abre 7h"
 *    sao texto; a cor e reforco.
 *
 * ## E uma quarta, que e o defeito desta rodada: a folha NAO troca a filial sozinha
 *
 * Trocar de filial com o carrinho cheio nao e mudar um rotulo. `DOMAIN` INV-C1 — *"uma filial
 * por carrinho [...] trocar de filial exige `SetStore`, que revalida todas as linhas e devolve
 * a lista de linhas perdidas"* — e RN-076 — *"trocar de filial revalida tudo e informa
 * perdas"*. Antes desta rodada a folha chamava `escolher()` e pronto: o cabecalho passava a
 * mostrar a filial nova e `GET /v1/cart` continuava devolvendo a antiga, sem uma palavra na
 * tela. O contrato ja previa o conflito (`409 CART_STORE_MISMATCH`, `API §6.20.5`); quem estava
 * calado era a interface.
 *
 * Agora a folha tem **tres passos** (`troca-de-filial.ts` tem a regra, `TrocaDeFilial.tsx` tem
 * os pedacos de tela):
 *
 * ```text
 *  LISTA --(outra filial, carrinho com itens)--> CONFIRMAR --PUT /v1/cart--> RESUMO
 *    +----(mesma filial do carrinho, ou carrinho vazio)----> escolher() e fecha
 * ```
 *
 * Passos **dentro da mesma folha**, e nao um modal por cima: `§4.3`, "profundidade maxima de
 * camada = 1".
 *
 * **A ordem importa e e a correcao inteira:** `escolher()` so acontece DEPOIS de o `PUT` voltar
 * `200`. Trocar o cabecalho primeiro e reintroduzir a divergencia sempre que a escrita falhar.
 */

import { queryKeys, useApiClient, useApiQueries } from '@farmacia/api-client/query';
import type { CartResponse, StoreOpenStatus, StoreSummary } from '@farmacia/contracts';
import {
  Badge,
  Button,
  ErrorState,
  Sheet,
  Skeleton,
  formatarHoraEMinuto,
  useAnnounce,
} from '@farmacia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';

import { descreverErro } from '../app/erros.js';
import { texto } from '../conteudo/index.js';

import { useMinhaLoja } from './contexto.js';
import type { LojaPreferida } from './preferencia.js';
import {
  CorpoDaConfirmacaoDeTroca,
  ListaDeMudancas,
  RodapeDaConfirmacaoDeTroca,
} from './TrocaDeFilial.jsx';
import {
  corpoDaTrocaDeFilial,
  diferencaDoCarrinho,
  houveMudanca,
  type DiferencaDoCarrinho,
} from './troca-de-filial.js';

// ---------------------------------------------------------------------------
// O botao do cabecalho
// ---------------------------------------------------------------------------

export function BotaoDeLoja(): ReactNode {
  const { loja, abrirSeletor, seletorAberto } = useMinhaLoja();

  // Sem filial escolhida o botao **convida**; com filial, ele informa e oferece a troca. Os dois
  // rotulos acessiveis dizem a acao, nao o estado: "Escolher loja" / "Trocar de loja".
  const rotuloAcessivel =
    loja === null ? texto('loja.cabecalho.escolher') : texto('loja.cabecalho.trocar');
  const visivel = loja === null || loja.nome === '' ? texto('loja.cabecalho.escolher') : loja.nome;

  return (
    <Button
      variant="ghost"
      size="md"
      className="cabecalho-loja__botao"
      onClick={abrirSeletor}
      aria-haspopup="dialog"
      aria-expanded={seletorAberto}
      aria-label={rotuloAcessivel}
      iconStart="map-pin"
      iconEnd="chevron-down"
    >
      <span className="cabecalho-loja__nome">{visivel}</span>
    </Button>
  );
}

// ---------------------------------------------------------------------------
// A folha de escolha
// ---------------------------------------------------------------------------

/**
 * O passo em que a folha esta.
 *
 * `alvo` viaja no proprio passo, e nao num estado ao lado: um `alvo` que pudesse existir sem
 * passo (ou um passo `CONFIRMAR` sem `alvo`) seria um estado impossivel representavel, e o
 * primeiro `alvo!` do arquivo.
 */
type PassoDaFolha =
  | { readonly nome: 'LISTA' }
  | { readonly nome: 'CONFIRMAR'; readonly alvo: LojaPreferida }
  | {
      readonly nome: 'RESUMO';
      readonly alvo: LojaPreferida;
      readonly diferenca: DiferencaDoCarrinho;
    };

export function FolhaDeEscolhaDeLoja(): ReactNode {
  const { loja, escolher, seletorAberto, fecharSeletor } = useMinhaLoja();
  const queries = useApiQueries();
  const cliente = useApiClient();
  const queryClient = useQueryClient();
  const { anunciar } = useAnnounce();
  const consulta = useQuery(queries.stores.list());

  /**
   * O carrinho, lido **so enquanto a folha esta aberta**.
   *
   * Ele nao pode morar no cabecalho: `GET /v1/cart` e `PRIV, no-store` e poria uma requisicao
   * privada no caminho critico de toda tela, inclusive das publicas. Aqui ele responde a um
   * gesto explicito — a pessoa tocou em "Trocar de loja" — e sem ele nao ha como saber se a
   * troca custa alguma coisa.
   *
   * Falha de leitura **nao** vira erro de tela: sem carrinho conhecido, a troca segue o caminho
   * antigo (escolhe e fecha). Bloquear a escolha de filial porque o carrinho nao carregou seria
   * trocar um defeito por outro pior.
   */
  const consultaDoCarrinho = useQuery({ ...queries.cart.current(), enabled: seletorAberto });
  const carrinho: CartResponse | undefined = consultaDoCarrinho.data;

  const [passo, setPasso] = useState<PassoDaFolha>({ nome: 'LISTA' });

  const erro = descreverErro(consulta.error);
  const filiais = consulta.data?.data ?? [];

  /** O nome de vitrine da filial em que o carrinho esta hoje — `§7.7`: "dizer o estado atual". */
  const nomeDaFilialDoCarrinho =
    filiais.find((filial) => filial.id === carrinho?.storeId)?.name ??
    (carrinho?.storeId === loja?.id ? loja?.nome : undefined) ??
    texto('loja.troca.semNome');

  const troca = useMutation({
    mutationFn: ({ alvo, atual }: { readonly alvo: LojaPreferida; readonly atual: CartResponse }) =>
      cliente.cart.replace(corpoDaTrocaDeFilial(atual, alvo.id)),
    onSuccess: (novo, { alvo, atual }) => {
      const diferenca = diferencaDoCarrinho(atual.items, novo.items);
      // A resposta do `PUT` **e** o carrinho atual: semear o cache evita um `GET` redundante e,
      // mais importante, impede que a tela de carrinho pisque a filial velha por um quadro.
      queryClient.setQueryData(queryKeys.cart.current(), novo);
      // So agora. Ver o cabecalho do arquivo: cabecalho antes da escrita e a divergencia de volta.
      escolher(alvo);

      if (!houveMudanca(diferenca)) {
        anunciar(texto('loja.troca.semMudanca', { loja: alvo.nome }));
        setPasso({ nome: 'LISTA' });
        fecharSeletor();
        return;
      }
      anunciar(
        texto('loja.troca.resumo.anuncio', {
          loja: alvo.nome,
          quantidade: diferenca.perdidos.length + diferenca.reduzidos.length,
        }),
      );
      setPasso({ nome: 'RESUMO', alvo, diferenca });
    },
  });

  const fechar = (): void => {
    setPasso({ nome: 'LISTA' });
    troca.reset();
    fecharSeletor();
  };

  const aoEscolherFilial = (filial: StoreSummary): void => {
    const alvo: LojaPreferida = { id: filial.id, nome: filial.name };
    // Confirmar so quando ha o que perder: carrinho conhecido, com item, em OUTRA filial.
    // Pedir confirmacao para um carrinho vazio seria atrito puro, e o `§8` nao quer atrito —
    // quer que a perda apareca.
    if (carrinho !== undefined && carrinho.items.length > 0 && carrinho.storeId !== alvo.id) {
      setPasso({ nome: 'CONFIRMAR', alvo });
      return;
    }
    escolher(alvo);
    fechar();
  };

  const falhaDaTroca = descreverErro(troca.error);

  const titulo =
    passo.nome === 'CONFIRMAR'
      ? texto('loja.troca.titulo', { loja: passo.alvo.nome })
      : passo.nome === 'RESUMO'
        ? texto('loja.troca.resumo.titulo')
        : texto('loja.sheet.titulo');

  const descricao =
    passo.nome === 'CONFIRMAR'
      ? texto('loja.troca.descricao', { atual: nomeDaFilialDoCarrinho })
      : passo.nome === 'RESUMO'
        ? texto('loja.troca.resumo.descricao', { loja: passo.alvo.nome })
        : texto('loja.sheet.descricao');

  return (
    <Sheet
      open={seletorAberto}
      onOpenChange={(aberto) => {
        if (!aberto) fechar();
      }}
      title={titulo}
      description={descricao}
      size="half"
      footer={
        passo.nome === 'CONFIRMAR' ? (
          <RodapeDaConfirmacaoDeTroca
            nomeAlvo={passo.alvo.nome}
            nomeAtual={nomeDaFilialDoCarrinho}
            trocando={troca.isPending}
            aoManter={() => {
              troca.reset();
              setPasso({ nome: 'LISTA' });
            }}
            aoConfirmar={() => {
              if (carrinho === undefined) return;
              troca.mutate({ alvo: passo.alvo, atual: carrinho });
            }}
          />
        ) : passo.nome === 'RESUMO' ? (
          <div className="acoes-fixas">
            <Button variant="primary" size="lg" fullWidth onClick={fechar}>
              {texto('loja.troca.resumo.entendi')}
            </Button>
          </div>
        ) : undefined
      }
    >
      {passo.nome === 'CONFIRMAR' ? (
        <>
          {falhaDaTroca === null ? null : (
            <ErrorState
              pictogram="erro"
              title={texto('loja.troca.erro.titulo')}
              description={`${falhaDaTroca.mensagem} ${texto('loja.troca.erro.descricao', {
                atual: nomeDaFilialDoCarrinho,
              })}`}
              {...(falhaDaTroca.codigo === undefined ? {} : { code: falhaDaTroca.codigo })}
            />
          )}
          <CorpoDaConfirmacaoDeTroca nomeAlvo={passo.alvo.nome} itens={carrinho?.items ?? []} />
        </>
      ) : passo.nome === 'RESUMO' ? (
        <ListaDeMudancas diferenca={passo.diferenca} nomeDaLoja={passo.alvo.nome} />
      ) : consulta.isPending ? (
        <div role="group" aria-busy="true" aria-label={texto('loja.carregando')}>
          <Skeleton shape="row" lines={3} />
        </div>
      ) : erro !== null ? (
        <ErrorState
          pictogram="erro"
          title={texto('loja.erro.titulo')}
          description={erro.mensagem}
          {...(erro.codigo === undefined ? {} : { code: erro.codigo })}
          onRetry={() => void consulta.refetch()}
        />
      ) : filiais.length === 0 ? (
        <ErrorState
          pictogram="loja-fechada"
          title={texto('loja.vazia.titulo')}
          description={texto('loja.vazia.descricao')}
        />
      ) : (
        <ul className="lista-de-lojas">
          {filiais.map((filial) => (
            <li key={filial.id}>
              <ItemDeLoja
                filial={filial}
                atual={filial.id === loja?.id}
                aoEscolher={() => {
                  aoEscolherFilial(filial);
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}

interface ItemDeLojaProps {
  readonly filial: StoreSummary;
  readonly atual: boolean;
  readonly aoEscolher: () => void;
}

function ItemDeLoja({ filial, atual, aoEscolher }: ItemDeLojaProps): ReactNode {
  const { texto: rotuloDoStatus, tom } = descreverStatus(filial.status);

  return (
    <Button
      variant={atual ? 'secondary' : 'tertiary'}
      size="lg"
      fullWidth
      className="lista-de-lojas__item"
      onClick={aoEscolher}
      // O rotulo acessivel diz a ACAO com o nome dentro; o visivel mostra nome, endereco e
      // status. Sem isto, o leitor de tela anuncia so "Filial Centro", que nao e um botao.
      aria-label={texto('loja.sheet.escolherEsta', { loja: filial.name })}
      {...(atual ? { 'aria-current': 'true' as const } : {})}
    >
      <span className="lista-de-lojas__conteudo">
        <span className="lista-de-lojas__nome">{filial.name}</span>
        <span className="lista-de-lojas__endereco">
          {`${filial.address.street}${filial.address.number === null ? '' : `, ${filial.address.number}`}`}
        </span>
        <Badge tone={tom}>{rotuloDoStatus}</Badge>
        {atual ? (
          <span className="lista-de-lojas__atual">{texto('loja.sheet.atual')}</span>
        ) : null}
      </span>
    </Button>
  );
}

/**
 * `StoreOpenStatus` -> texto e tom, na ordem em que o §7.2 lista os estados.
 *
 * A ordem importa: uma filial pausada tambem esta `open`, e anunciar "Aberta ate 22h" para quem
 * nao consegue pedir e a mentira mais cara desta tela.
 */
export function descreverStatus(status: StoreOpenStatus): {
  readonly texto: string;
  readonly tom: 'brand' | 'neutral' | 'info' | 'danger';
} {
  if (status.paused) return { texto: texto('loja.status.pausada'), tom: 'danger' };
  if (status.open && status.onCall) {
    return { texto: texto('loja.status.plantao'), tom: 'info' };
  }
  if (status.open) {
    return status.closesAt === null
      ? { texto: texto('loja.status.aberta'), tom: 'brand' }
      : {
          texto: texto('loja.status.abertaAte', { hora: formatarHoraEMinuto(status.closesAt) }),
          tom: 'brand',
        };
  }
  return status.opensAt === null
    ? { texto: texto('loja.status.fechada'), tom: 'neutral' }
    : {
        texto: texto('loja.status.fechadaAbre', { hora: formatarHoraEMinuto(status.opensAt) }),
        tom: 'neutral',
      };
}

// ---------------------------------------------------------------------------
// Selecao automatica quando nao ha escolha a fazer
// ---------------------------------------------------------------------------

/**
 * Uma filial so nao e uma escolha — ver a regra 4 no cabecalho de `contexto.tsx`.
 *
 * Componente sem marcacao, montado uma vez na raiz de composicao (`app/providers.tsx`), porque
 * e la que o cliente HTTP existe. Ele **so** consulta enquanto nao ha filial: com filial
 * guardada, `enabled` e `false` e nao ha requisicao nenhuma no boot.
 */
export function SelecaoAutomaticaDeLoja(): ReactNode {
  const { loja, hidratando, escolher } = useMinhaLoja();
  const queries = useApiQueries();

  const precisa = loja === null && !hidratando;
  const consulta = useQuery({ ...queries.stores.list(), enabled: precisa });

  const filiais = consulta.data?.data;
  const unica = filiais?.length === 1 ? filiais[0] : undefined;

  useEffect(() => {
    if (!precisa || unica === undefined) return;
    escolher({ id: unica.id, nome: unica.name });
  }, [precisa, unica, escolher]);

  return null;
}
